import { HISTORY_FULFILLMENT_STATUSES } from "./marketplace-fulfillment.js";

/** Curated production capabilities shown as catalog tags and filter chips. */
export const VENDOR_CAPABILITIES = [
  "ugc",
  "motion",
  "voiceover",
  "subtitles",
  "animation",
  "photography",
  "ai",
] as const;

export type VendorCapability = (typeof VENDOR_CAPABILITIES)[number];

export const VENDOR_PRICE_TIERS = ["budget", "mid", "premium"] as const;
export type VendorPriceTier = (typeof VENDOR_PRICE_TIERS)[number];

export const VENDOR_TURNAROUND_BUCKETS = ["fast", "standard", "longer"] as const;
export type VendorTurnaroundBucket = (typeof VENDOR_TURNAROUND_BUCKETS)[number];

export const VENDOR_AVAILABILITY = [
  "available",
  "paused",
  "unavailable",
] as const;
export type VendorAvailability = (typeof VENDOR_AVAILABILITY)[number];

/** Public on-time / revision rates stay hidden until this many completed orders. */
export const VENDOR_METRICS_MIN_ORDERS = 3;

export const VENDOR_TURNAROUND_FAST_MAX_DAYS = 7;
export const VENDOR_TURNAROUND_STANDARD_MAX_DAYS = 14;

export const VENDOR_PRICE_BUDGET_MAX = 2_000;
export const VENDOR_PRICE_MID_MAX = 8_000;

const AI_CAPABILITY_PATTERN =
  /\b(?:ai[- ]generated|ai[- ]powered|generative[- ]ai|artificial[- ]intelligence)\b/;

const CAPABILITY_KEYWORDS: Record<Exclude<VendorCapability, "ai">, readonly string[]> = {
  ugc: ["ugc", "user generated", "creator", "testimonial"],
  motion: ["motion", "motion graphics", "after effects", "animation gfx"],
  voiceover: ["voiceover", "voice-over", "voice over", "vo ", "narrat"],
  subtitles: ["subtitle", "caption", "captions", "subtitles"],
  animation: ["animat", "2d", "3d", "illustrated"],
  photography: ["photo", "still", "product shot"],
};

export interface VendorCatalogPackageInput {
  name: string;
  description?: string | null;
  price?: number | null;
  isActive?: boolean;
  defaultDeliveryDays?: number | null;
}

export interface VendorCatalogTextInput {
  name?: string;
  description?: string | null;
  positioning?: string | null;
  capabilities?: readonly string[] | null;
  packages?: readonly VendorCatalogPackageInput[];
  defaultDeliveryDays?: number | null;
}

export interface VendorMetricOrderInput {
  vendorId: string;
  fulfillmentStatus: string;
  updatedAt: string;
  etaDate?: string | null;
  scripts: readonly {
    fulfillmentStatus: string;
    revisionNote?: string | null;
    expectedDeliveryDate?: string | null;
  }[];
}

export interface VendorOrderMetrics {
  orderCount: number;
  completedOrderCount: number;
  scriptCount: number;
  onTimeRate: number | null;
  revisionRate: number | null;
  lastUsedAt: string | null;
}

function tokenize(value: string): string {
  return value.toLowerCase();
}

export function isVendorCapability(value: string): value is VendorCapability {
  return (VENDOR_CAPABILITIES as readonly string[]).includes(value);
}

export function uniqueVendorCapabilities(
  values: readonly string[] | null | undefined,
): VendorCapability[] {
  return [...new Set((values ?? []).filter(isVendorCapability))];
}

/**
 * Prefer persisted capabilities; otherwise infer up to 4 tags from name,
 * positioning, description, and package copy.
 */
export function resolveVendorCapabilities(
  input: VendorCatalogTextInput,
): VendorCapability[] {
  const persisted = uniqueVendorCapabilities(input.capabilities);
  if (persisted.length > 0) {
    return persisted;
  }

  const haystack = tokenize(
    [
      input.name,
      input.positioning,
      input.description,
      ...(input.packages ?? []).flatMap((pkg) => [pkg.name, pkg.description]),
    ]
      .filter(Boolean)
      .join(" "),
  );

  const inferred = VENDOR_CAPABILITIES.filter((capability) => {
    if (capability === "ai") {
      return AI_CAPABILITY_PATTERN.test(haystack);
    }
    return CAPABILITY_KEYWORDS[capability].some((keyword) => haystack.includes(keyword));
  });
  return inferred.slice(0, 4);
}

export function resolveVendorPositioning(
  input: VendorCatalogTextInput,
): string | null {
  const explicit = input.positioning?.trim();
  if (explicit) return explicit;
  const description = input.description?.trim();
  if (!description) return null;
  const firstLine = description.split(/\n/)[0]?.trim() ?? "";
  return firstLine.length > 0 ? firstLine : null;
}

export function resolvePriceFrom(
  packages: readonly VendorCatalogPackageInput[] | undefined,
): number | null {
  const prices = (packages ?? [])
    .filter((pkg) => pkg.isActive !== false && pkg.price != null)
    .map((pkg) => pkg.price!)
    .filter((price) => Number.isFinite(price));
  if (prices.length === 0) return null;
  return Math.min(...prices);
}

export function resolveTurnaroundDays(
  input: VendorCatalogTextInput,
): number | null {
  if (input.defaultDeliveryDays != null && input.defaultDeliveryDays > 0) {
    return input.defaultDeliveryDays;
  }
  const packageDays = (input.packages ?? [])
    .map((pkg) => pkg.defaultDeliveryDays)
    .filter((days): days is number => days != null && days > 0);
  if (packageDays.length === 0) return null;
  return Math.min(...packageDays);
}

export function resolvePriceTier(priceFrom: number | null): VendorPriceTier | null {
  if (priceFrom == null) return null;
  if (priceFrom < VENDOR_PRICE_BUDGET_MAX) return "budget";
  if (priceFrom < VENDOR_PRICE_MID_MAX) return "mid";
  return "premium";
}

export function resolveTurnaroundBucket(
  days: number | null,
): VendorTurnaroundBucket | null {
  if (days == null) return null;
  if (days <= VENDOR_TURNAROUND_FAST_MAX_DAYS) return "fast";
  if (days <= VENDOR_TURNAROUND_STANDARD_MAX_DAYS) return "standard";
  return "longer";
}

export function resolveVendorAvailability(input: {
  isActive: boolean;
  canReceiveOrders?: boolean;
}): VendorAvailability {
  if (!input.isActive) return "paused";
  if (input.canReceiveOrders === false) return "unavailable";
  return "available";
}

function isCompletedStatus(status: string): boolean {
  return (HISTORY_FULFILLMENT_STATUSES as readonly string[]).includes(status);
}

function dateOnly(iso: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return match?.[1] ?? null;
}

/**
 * On-time % and revision rate from this tenant's orders.
 *
 * On-time: terminal orders whose `updatedAt` date is on or before the
 * order `etaDate`, or the latest script `expectedDeliveryDate` when no ETA.
 * Revision: scripts that are `SENT_FOR_REVISION` or carry a revision note,
 * over all scripts on the vendor's orders.
 *
 * Rates stay `null` until `VENDOR_METRICS_MIN_ORDERS` completed orders.
 *
 * Prisma shape for a later BFF aggregation (same predicates):
 * ```
 * prisma.marketplaceOrder.findMany({
 *   where: { tenantId, vendorId, deletedAt: null },
 *   select: {
 *     fulfillmentStatus: true,
 *     updatedAt: true,
 *     etaDate: true,
 *     scripts: { select: { fulfillmentStatus: true, revisionNote: true, expectedDeliveryDate: true } },
 *   },
 * })
 * ```
 */
export function computeVendorOrderMetrics(
  vendorId: string,
  orders: readonly VendorMetricOrderInput[],
): VendorOrderMetrics {
  const vendorOrders = orders.filter((order) => order.vendorId === vendorId);
  const completed = vendorOrders.filter((order) =>
    isCompletedStatus(order.fulfillmentStatus),
  );

  let onTimeEligible = 0;
  let onTimeHits = 0;
  for (const order of completed) {
    const deadline =
      dateOnly(order.etaDate ?? "") ??
      order.scripts
        .map((script) => dateOnly(script.expectedDeliveryDate ?? ""))
        .filter((value): value is string => value != null)
        .sort()
        .at(-1) ??
      null;
    if (!deadline) continue;
    const delivered = dateOnly(order.updatedAt);
    if (!delivered) continue;
    onTimeEligible += 1;
    if (delivered <= deadline) onTimeHits += 1;
  }

  const scripts = vendorOrders.flatMap((order) => order.scripts);
  const revised = scripts.filter(
    (script) =>
      script.fulfillmentStatus === "SENT_FOR_REVISION" ||
      Boolean(script.revisionNote?.trim()),
  ).length;

  const lastUsedAt =
    vendorOrders
      .map((order) => order.updatedAt)
      .sort()
      .at(-1) ?? null;

  const hasPublicRates = completed.length >= VENDOR_METRICS_MIN_ORDERS;

  return {
    orderCount: vendorOrders.length,
    completedOrderCount: completed.length,
    scriptCount: scripts.length,
    onTimeRate:
      hasPublicRates && onTimeEligible > 0 ? onTimeHits / onTimeEligible : null,
    revisionRate: hasPublicRates && scripts.length > 0 ? revised / scripts.length : null,
    lastUsedAt,
  };
}
